import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Send, Clock, CheckCircle, XCircle, Instagram, Facebook, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLanguage } from "../lib/language";

// Panel embedded in Profile ("Ser Socio" tab).
// - Users without blue-check → shown a soft blocker with a link to verification.
// - Verified users without an active partner code → shown the application form
//   (or the status of their latest submission if it's pending/rejected).
// - Verified users who are already partners → shouldn't see this tab at all
//   (Profile.jsx hides it when `partnerInfo` is set).
export default function BecomePartnerPanel({ user }) {
    const { t } = useLanguage();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/partner-applications/mine");
            setApplications(data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // The latest application (most recent first from the API).
    const latest = applications[0];
    // Only block re-application while a submission is still pending review. Approved
    // applications whose partner was later deleted, or rejected ones, should let the
    // user try again.
    const canReapply = !latest || latest.status !== "pending";

    // Not verified → gate.
    if (!user.verified) {
        return (
            <div className="tb-card p-6 text-center space-y-3" data-testid="become-partner-gate">
                <BadgeCheck className="w-10 h-10 text-[#2196F3] mx-auto" />
                <h3 className="font-display text-xl">{t("Verifica tu identidad primero", "Verify your identity first")}</h3>
                <p className="text-sm text-[var(--tb-text-2)] max-w-md mx-auto">
                    {t(
                        "Para postularte como socio necesitas tener el check azul. Sube una selfie y una foto de tu documento desde la pestaña de verificación.",
                        "To apply as a partner you need the blue check. Upload a selfie and an ID photo from the verification tab."
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="become-partner-panel">
            {/* Intro */}
            <div className="tb-card p-5">
                <h3 className="font-display text-xl mb-2">{t("Ser socio de Terra Boutique", "Become a Terra Boutique partner")}</h3>
                <p className="text-sm text-[var(--tb-text-2)] leading-relaxed">
                    {t(
                        "Como socio recibes un código único que compartes con tus amigos y seguidores. Cada compra hecha con ese código se acredita a tu cuenta — y según el modo que acordemos, puedes ganar comisiones sobre cada venta.",
                        "As a partner you get a unique code to share with friends and followers. Every purchase made with that code is credited to your account — and depending on the mode we agree, you can earn a commission on each sale."
                    )}
                </p>
            </div>

            {/* Latest application status (if any) */}
            {loading ? (
                <div className="text-xs text-[var(--tb-muted)]">{t("Cargando…", "Loading…")}</div>
            ) : latest && (
                <ApplicationStatusCard app={latest} />
            )}

            {/* Application form — hidden if we already have a pending application */}
            {canReapply && <ApplicationForm onSubmitted={load} />}
        </div>
    );
}


function ApplicationStatusCard({ app }) {
    const { t } = useLanguage();
    const cfg = {
        pending: { icon: Clock, tone: "bg-amber-500/15 border-amber-400/40 text-amber-300",
                   label: t("Postulación en revisión", "Application under review") },
        approved: { icon: CheckCircle, tone: "bg-emerald-500/15 border-emerald-400/40 text-emerald-300",
                    label: t("Aprobada", "Approved") },
        rejected: { icon: XCircle, tone: "bg-rose-500/15 border-rose-400/40 text-rose-300",
                    label: t("Rechazada", "Rejected") },
    }[app.status] || { icon: Clock, tone: "bg-[var(--tb-bg-2)] border-[var(--tb-border)] text-[var(--tb-muted)]", label: app.status };
    const Icon = cfg.icon;
    return (
        <div className={`tb-card p-5 border ${cfg.tone}`} data-testid={`app-status-${app.status}`}>
            <div className="flex items-center gap-3 mb-2">
                <Icon className="w-5 h-5" />
                <div className="font-display text-lg">{cfg.label}</div>
                <div className="text-[10px] opacity-70 ml-auto">{new Date(app.created_at).toLocaleDateString("es-SV")}</div>
            </div>
            <div className="text-xs opacity-90 space-y-1">
                <div><strong>{t("Código sugerido:", "Suggested code:")}</strong> <span className="font-mono">{app.code_suggested}</span></div>
                <div className="italic">&ldquo;{app.motivation}&rdquo;</div>
                {app.admin_note && (
                    <div className="mt-2 pt-2 border-t border-current/20">
                        <strong>{t("Nota del equipo:", "Team note:")}</strong> {app.admin_note}
                    </div>
                )}
            </div>
        </div>
    );
}


function ApplicationForm({ onSubmitted }) {
    const { t } = useLanguage();
    const [code, setCode] = useState("");
    const [motivation, setMotivation] = useState("");
    const [audience, setAudience] = useState("");
    const [plan, setPlan] = useState("");
    const [instagram, setInstagram] = useState("");
    const [tiktok, setTiktok] = useState("");
    const [facebook, setFacebook] = useState("");
    const [phone, setPhone] = useState("");
    // Bank info collected upfront to enable weekly commission payouts.
    const [bankName, setBankName] = useState("");
    const [bankType, setBankType] = useState("Ahorros");
    const [bankNumber, setBankNumber] = useState("");
    const [bankHolder, setBankHolder] = useState("");
    // Structured SV address (same fields used in Checkout) + reference.
    const [addressStreet, setAddressStreet] = useState("");
    const [addressReference, setAddressReference] = useState("");
    const [departamentoId, setDepartamentoId] = useState("");
    const [municipioId, setMunicipioId] = useState("");
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [loadingDeptos, setLoadingDeptos] = useState(false);
    const [loadingMunis, setLoadingMunis] = useState(false);
    // National ID document — required for auditing partner payouts.
    const [documentType, setDocumentType] = useState("DUI");
    const [documentNumber, setDocumentNumber] = useState("");
    const [saving, setSaving] = useState(false);

    // Load SV C807 department catalog (same public endpoint used in Checkout).
    const loadDepartamentos = useCallback(async () => {
        setLoadingDeptos(true);
        try {
            const { data } = await api.get("/c807-public/catalogo/departamentos");
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setDepartamentos(list);
        } catch (err) {
            console.warn("[partner-app] error loading departamentos:", err?.message);
            setDepartamentos([]);
        } finally { setLoadingDeptos(false); }
    }, []);
    useEffect(() => { loadDepartamentos(); }, [loadDepartamentos]);

    const onPickDepartamento = async (depIdRaw) => {
        const depId = depIdRaw === "" || depIdRaw == null ? "" : Number(depIdRaw);
        setDepartamentoId(depId);
        setMunicipioId("");
        setMunicipios([]);
        if (!depId || Number.isNaN(depId)) return;
        setLoadingMunis(true);
        try {
            const { data } = await api.get(`/c807-public/catalogo/municipios?departamento=${depId}`);
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setMunicipios(list);
        } catch (err) {
            console.warn("[partner-app] error loading municipios:", err?.message);
            setMunicipios([]);
            toast.error(t("No se pudo cargar la lista de distritos. Intenta de nuevo.", "Could not load the list of districts. Please try again."));
        } finally { setLoadingMunis(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (motivation.trim().length < 20) {
            toast.error(t("Cuéntanos tu motivación con al menos 20 caracteres.", "Tell us your motivation in at least 20 characters."));
            return;
        }
        if (!addressStreet.trim()) {
            toast.error(t("Ingresa tu calle y número de casa", "Enter your street and house number"));
            return;
        }
        if (!departamentoId) {
            toast.error(t("Selecciona tu departamento", "Select your department"));
            return;
        }
        if (!municipioId) {
            toast.error(t("Selecciona tu municipio / distrito", "Select your municipality / district"));
            return;
        }
        if (!documentNumber.trim() || documentNumber.trim().length < 5) {
            toast.error(t("Ingresa un número de documento válido", "Enter a valid document number"));
            return;
        }
        setSaving(true);
        try {
            const social_links = {};
            if (instagram.trim()) social_links.instagram = instagram.trim();
            if (tiktok.trim()) social_links.tiktok = tiktok.trim();
            if (facebook.trim()) social_links.facebook = facebook.trim();
            await api.post("/partner-applications", {
                code_suggested: code,
                motivation,
                audience_size: audience,
                promotion_plan: plan,
                social_links,
                contact_phone: phone,
                bank_name: bankName,
                bank_account_type: bankType,
                bank_account_number: bankNumber,
                bank_account_holder: bankHolder,
                address_street: addressStreet,
                departamento_id: departamentoId || null,
                municipio_id: municipioId || null,
                address_reference: addressReference,
                document_type: documentType,
                document_number: documentNumber,
            });
            toast.success(t("¡Postulación enviada! Te avisaremos por email.", "Application submitted! We'll email you."));
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("Error al enviar", "Error submitting"));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="tb-card p-5 space-y-4" data-testid="become-partner-form">
            <h3 className="font-display text-lg">{t("Postúlate ahora", "Apply now")}</h3>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Código que te gustaría *", "Code you'd like *")}</label>
                <input className="tb-input mt-1 uppercase font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="EJ. JULIO25" maxLength={30} required data-testid="app-code" />
                <p className="text-[10px] text-[var(--tb-muted)] mt-1">{t("El equipo puede ajustarlo si es necesario.", "The team may adjust it if needed.")}</p>
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("¿Por qué quieres ser socio? *", "Why do you want to be a partner? *")}</label>
                <textarea className="tb-input mt-1 min-h-[80px]" value={motivation} onChange={(e) => setMotivation(e.target.value)}
                    placeholder={t("Cuéntanos tu conexión con Terra Boutique, tu audiencia, tu estilo...", "Tell us your connection with Terra Boutique, your audience, your style...")}
                    minLength={20} required data-testid="app-motivation" />
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 flex items-center gap-1">
                    🔒 {t("Este texto es privado: solo lo lee el equipo de revisión. Nunca se publica en tu tarjeta pública.", "This text is private: only the review team reads it. It's never shown on your public card.")}
                </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">{t("Tamaño de tu audiencia", "Your audience size")}</label>
                    <input className="tb-input mt-1" value={audience} onChange={(e) => setAudience(e.target.value)}
                        placeholder={t("Ej. ~8k seguidores Instagram", "E.g. ~8k Instagram followers")} data-testid="app-audience" />
                </div>
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">{t("Teléfono de contacto", "Contact phone")}</label>
                    <input className="tb-input mt-1" value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="+503 ..." data-testid="app-phone" />
                </div>
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Plan breve de promoción", "Short promotion plan")}</label>
                <textarea className="tb-input mt-1 min-h-[60px]" value={plan} onChange={(e) => setPlan(e.target.value)}
                    placeholder={t("Ej. 2 stories por semana + 1 reel al mes con los productos", "E.g. 2 stories per week + 1 monthly reel featuring the products")}
                    data-testid="app-plan" />
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Tus redes sociales (opcional)", "Your social media (optional)")}</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-1">
                    <div className="relative">
                        <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="@usuario" value={instagram} onChange={(e) => setInstagram(e.target.value)} data-testid="app-instagram" />
                    </div>
                    <div className="relative">
                        <ExternalLink className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="TikTok" value={tiktok} onChange={(e) => setTiktok(e.target.value)} data-testid="app-tiktok" />
                    </div>
                    <div className="relative">
                        <Facebook className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="Facebook" value={facebook} onChange={(e) => setFacebook(e.target.value)} data-testid="app-facebook" />
                    </div>
                </div>
            </div>

            {/* Address block (same structure as Checkout SV form). */}
            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Dirección (para envío de premios / correspondencia)", "Address (for prize deliveries / correspondence)")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2 flex items-center gap-1">
                    🔒 {t("Solo el equipo administrativo ve esta información.", "Only the admin team sees this information.")}
                </p>
                <div className="space-y-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Calle y número de casa *", "Street and house number *")}</label>
                        <input className="tb-input mt-1" value={addressStreet} onChange={(e) => setAddressStreet(e.target.value)}
                            placeholder={t("Ej. Calle Principal #123", "E.g. 123 Main Street")} maxLength={140}
                            data-testid="app-address-street" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline text-[10px]">{t("Departamento *", "Department *")}</label>
                            <div className="relative">
                                <select className="tb-input mt-1"
                                    value={departamentoId === "" ? "" : String(departamentoId)}
                                    onChange={(e) => onPickDepartamento(e.target.value)}
                                    disabled={loadingDeptos}
                                    data-testid="app-departamento" required>
                                    <option value="">{loadingDeptos ? t("Cargando...", "Loading...") : (departamentos.length === 0 ? t("Sin opciones — reintenta", "No options — retry") : t("Selecciona...", "Select..."))}</option>
                                    {departamentos.map((d) => <option key={d.id} value={String(d.id)}>{d.nombre}</option>)}
                                </select>
                                {departamentos.length === 0 && !loadingDeptos && (
                                    <button type="button" onClick={loadDepartamentos}
                                        className="absolute -top-5 right-0 text-[10px] text-[var(--tb-gold)] hover:underline"
                                        data-testid="app-reload-departamentos">↻ {t("Reintentar", "Retry")}</button>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="tb-overline text-[10px]">{t("Municipio / Distrito *", "Municipality / District *")}</label>
                            <select className="tb-input mt-1"
                                value={municipioId === "" ? "" : String(municipioId)}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setMunicipioId(v === "" ? "" : Number(v));
                                }}
                                disabled={loadingMunis || !departamentoId || municipios.length === 0}
                                data-testid="app-municipio" required>
                                <option value="">
                                    {!departamentoId ? t("Elige departamento", "Choose department")
                                        : loadingMunis ? t("Cargando...", "Loading...")
                                        : municipios.length === 0 ? t("Sin distritos — reintenta", "No districts — retry")
                                        : t("Selecciona...", "Select...")}
                                </option>
                                {municipios.map((m) => <option key={m.id} value={String(m.id)}>{m.nombre}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Puntos de referencia", "Landmarks")} <span className="text-[var(--tb-muted)] normal-case tracking-normal">({t("opcional", "optional")})</span></label>
                        <textarea className="tb-input mt-1 min-h-[50px]"
                            placeholder={t("Ej. Frente al parque central · Casa color azul con portón blanco", "E.g. Across from the central park · Blue house with white gate")}
                            value={addressReference} onChange={(e) => setAddressReference(e.target.value)}
                            maxLength={240}
                            data-testid="app-address-reference" />
                    </div>
                </div>
            </div>

            {/* Identity document — required for tax/audit records. */}
            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Documento de identidad *", "Identity document *")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2 flex items-center gap-1">
                    🔒 {t("Requerido para trazabilidad de pagos de comisiones. Solo lo ve el equipo administrativo.", "Required for commission payout traceability. Only the admin team sees this.")}
                </p>
                <div className="grid sm:grid-cols-[140px_1fr] gap-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Tipo", "Type")}</label>
                        <select className="tb-input mt-1" value={documentType} onChange={(e) => setDocumentType(e.target.value)} data-testid="app-doc-type">
                            <option value="DUI">DUI</option>
                            <option value="Pasaporte">{t("Pasaporte", "Passport")}</option>
                        </select>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Número", "Number")}</label>
                        <input className="tb-input mt-1 font-mono uppercase" value={documentNumber}
                            onChange={(e) => setDocumentNumber(e.target.value.toUpperCase())}
                            placeholder={documentType === "DUI" ? "00000000-0" : "A12345678"}
                            maxLength={30} required
                            data-testid="app-doc-number" />
                    </div>
                </div>
            </div>

            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Datos bancarios (para pagos de comisión)", "Bank details (for commission payouts)")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2 flex items-center gap-1">
                    🔒 {t("Solo el equipo administrativo ve esta información. Se usa únicamente para transferir tus comisiones al cierre de cada ciclo semanal.", "Only the admin team sees this information. It's used solely to transfer your commissions when the weekly cycle closes.")}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Banco", "Bank")}</label>
                        <input className="tb-input mt-1" value={bankName} onChange={(e) => setBankName(e.target.value)}
                            placeholder={t("Ej. Banco Agrícola", "E.g. Chase")} maxLength={60}
                            data-testid="app-bank-name" />
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Tipo de cuenta", "Account type")}</label>
                        <select className="tb-input mt-1" value={bankType} onChange={(e) => setBankType(e.target.value)} data-testid="app-bank-type">
                            <option value="Ahorros">{t("Ahorros", "Savings")}</option>
                            <option value="Corriente">{t("Corriente", "Checking")}</option>
                        </select>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Número de cuenta", "Account number")}</label>
                        <input className="tb-input mt-1 font-mono" value={bankNumber} onChange={(e) => setBankNumber(e.target.value)}
                            placeholder="0000-0000-0000" maxLength={40}
                            data-testid="app-bank-number" />
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Titular de la cuenta", "Account holder")}</label>
                        <input className="tb-input mt-1" value={bankHolder} onChange={(e) => setBankHolder(e.target.value)}
                            placeholder={t("Nombre como aparece en la cuenta", "Name as it appears on the account")} maxLength={80}
                            data-testid="app-bank-holder" />
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-[var(--tb-border)]">
                <button type="submit" disabled={saving} className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="app-submit">
                    <Send className="w-4 h-4" /> {saving ? t("Enviando…", "Submitting…") : t("Enviar postulación", "Submit application")}
                </button>
            </div>
        </form>
    );
}
